import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Zap, Plus, Play, Pause, Trash2, Edit3, Clock, Target, ChevronRight,
    Loader2, CheckCircle, XCircle, AlertTriangle, BarChart3, RefreshCw, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQ_LABELS = { hourly: 'Every Hour', daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
const TARGET_LABELS = { deals: 'Deals', contacts: 'Contacts', accounts: 'Companies', activities: 'Activities' };
const PRIORITY_COLORS = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-emerald-100 text-emerald-700' };
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function AIHeartbeat() {
    const { getAuthHeader } = useAuth();
    const [heartbeats, setHeartbeats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [showRunsModal, setShowRunsModal] = useState(null);
    const [runs, setRuns] = useState([]);
    const [runsLoading, setRunsLoading] = useState(false);
    const [showInsightsModal, setShowInsightsModal] = useState(null);
    const [builtins, setBuiltins] = useState([]);

    const loadHeartbeats = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/heartbeat/`, { headers: getAuthHeader() });
            setHeartbeats(res.data);
        } catch { toast.error('Failed to load heartbeats'); }
        finally { setLoading(false); }
    }, [getAuthHeader]);

    useEffect(() => { loadHeartbeats(); }, [loadHeartbeats]);

    useEffect(() => {
        axios.get(`${API}/heartbeat/builtins`, { headers: getAuthHeader() })
            .then(res => setBuiltins(res.data))
            .catch(() => {});
    }, [getAuthHeader]);

    const toggleActive = async (hb) => {
        try {
            await axios.put(`${API}/heartbeat/${hb.heartbeat_id}`, { is_active: !hb.is_active }, { headers: getAuthHeader() });
            loadHeartbeats();
        } catch { toast.error('Failed to toggle'); }
    };

    const deleteHeartbeat = async (id) => {
        if (!window.confirm('Delete this heartbeat automation?')) return;
        try {
            await axios.delete(`${API}/heartbeat/${id}`, { headers: getAuthHeader() });
            toast.success('Deleted');
            loadHeartbeats();
        } catch { toast.error('Failed to delete'); }
    };

    const triggerRun = async (hb) => {
        try {
            await axios.post(`${API}/heartbeat/${hb.heartbeat_id}/run`, {}, { headers: getAuthHeader() });
            toast.success(`"${hb.name}" triggered! Results will appear in a moment.`);
            // Reload after a few seconds
            setTimeout(loadHeartbeats, 5000);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to trigger'); }
    };

    const viewRuns = async (hb) => {
        setShowRunsModal(hb);
        setRunsLoading(true);
        try {
            const res = await axios.get(`${API}/heartbeat/${hb.heartbeat_id}/runs`, { headers: getAuthHeader() });
            setRuns(res.data);
        } catch { toast.error('Failed to load runs'); }
        finally { setRunsLoading(false); }
    };

    const formatSchedule = (sched) => {
        if (!sched) return 'Not set';
        const freq = sched.frequency || 'daily';
        const h = String(sched.hour ?? 8).padStart(2, '0');
        const m = String(sched.minute ?? 0).padStart(2, '0');
        if (freq === 'hourly') return `Every hour at :${m}`;
        if (freq === 'daily') return `Daily at ${h}:${m}`;
        if (freq === 'weekly') return `${WEEKDAYS[sched.day_of_week || 0]}s at ${h}:${m}`;
        if (freq === 'monthly') return `Monthly on day ${sched.day_of_month || 1} at ${h}:${m}`;
        return freq;
    };

    const statusIcon = (status) => {
        if (status === 'success') return <CheckCircle className="w-4 h-4 text-emerald-600" />;
        if (status === 'failed') return <XCircle className="w-4 h-4 text-red-500" />;
        if (status === 'running') return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
        return <Clock className="w-4 h-4 text-stone-400" />;
    };

    return (
        <div className="p-6 max-w-6xl mx-auto" data-testid="ai-heartbeat-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Heartbeat</h1>
                    <p className="text-sm text-stone-500 mt-1">Automated AI-powered CRM analysis running on your schedule</p>
                </div>
                <Button onClick={() => { setEditItem(null); setShowCreateModal(true); }} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="create-heartbeat-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Automation
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>
            ) : heartbeats.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                    <Zap className="w-16 h-16 text-stone-300 mb-4" />
                    <h2 className="text-lg font-semibold text-stone-700 mb-2">No automations yet</h2>
                    <p className="text-sm text-stone-500 mb-4 max-w-md">Set up AI-powered scans that run automatically — analyze deals, flag risks, find gaps, and surface insights on a schedule.</p>
                    <Button onClick={() => setShowCreateModal(true)} className="bg-stone-900 text-white hover:bg-stone-800">
                        <Plus className="w-4 h-4 mr-2" /> Create Your First Automation
                    </Button>
                </div>
            ) : (
                <div className="grid gap-4">
                    {heartbeats.map(hb => (
                        <div key={hb.heartbeat_id} className={`bg-white border rounded-xl p-5 transition-shadow hover:shadow-sm ${!hb.is_active ? 'opacity-60' : ''}`} data-testid={`heartbeat-${hb.heartbeat_id}`}>
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${hb.is_active ? 'bg-violet-50 text-violet-600' : 'bg-stone-100 text-stone-400'}`}>
                                        <Zap className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-stone-900">{hb.name}</h3>
                                        {hb.description && <p className="text-sm text-stone-500 mt-0.5 line-clamp-1">{hb.description}</p>}
                                        <div className="flex items-center gap-3 mt-2 text-xs text-stone-500 flex-wrap">
                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatSchedule(hb.schedule)}</span>
                                            <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {TARGET_LABELS[hb.scope?.target] || hb.scope?.target}</span>
                                            {hb.last_run_at && (
                                                <span className="flex items-center gap-1">
                                                    {statusIcon(hb.last_run_status)}
                                                    Last: {new Date(hb.last_run_at).toLocaleDateString()} {new Date(hb.last_run_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    {hb.last_run_insights > 0 && <span className="font-medium text-stone-700">({hb.last_run_insights} insights)</span>}
                                                </span>
                                            )}
                                            {hb.next_run_at && hb.is_active && (
                                                <span className="text-stone-400">Next: {new Date(hb.next_run_at).toLocaleDateString()} {new Date(hb.next_run_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="sm" onClick={() => triggerRun(hb)} title="Run Now" data-testid={`run-${hb.heartbeat_id}`}>
                                        <Play className="w-4 h-4 text-emerald-600" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => viewRuns(hb)} title="View Runs" data-testid={`runs-${hb.heartbeat_id}`}>
                                        <BarChart3 className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => toggleActive(hb)} title={hb.is_active ? 'Pause' : 'Resume'} data-testid={`toggle-${hb.heartbeat_id}`}>
                                        {hb.is_active ? <Pause className="w-4 h-4 text-amber-600" /> : <Play className="w-4 h-4 text-stone-400" />}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => { setEditItem(hb); setShowCreateModal(true); }} data-testid={`edit-${hb.heartbeat_id}`}>
                                        <Edit3 className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => deleteHeartbeat(hb.heartbeat_id)} className="text-red-500 hover:text-red-600" data-testid={`delete-${hb.heartbeat_id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <HeartbeatModal
                open={showCreateModal}
                onClose={() => { setShowCreateModal(false); setEditItem(null); }}
                existing={editItem}
                builtins={builtins}
                getAuthHeader={getAuthHeader}
                onSaved={loadHeartbeats}
            />

            {/* Runs History Modal */}
            <Dialog open={!!showRunsModal} onOpenChange={() => setShowRunsModal(null)}>
                <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="runs-modal">
                    <DialogHeader>
                        <DialogTitle>Run History — {showRunsModal?.name}</DialogTitle>
                        <DialogDescription>Past executions and insights</DialogDescription>
                    </DialogHeader>
                    {runsLoading ? (
                        <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>
                    ) : runs.length === 0 ? (
                        <p className="py-8 text-center text-sm text-stone-500">No runs yet. Click the play button to trigger one.</p>
                    ) : (
                        <div className="space-y-3">
                            {runs.map(run => (
                                <div key={run.run_id} className={`p-4 rounded-xl border ${run.status === 'failed' ? 'bg-red-50 border-red-100' : run.status === 'running' ? 'bg-blue-50 border-blue-100' : 'bg-stone-50 border-stone-100'}`} data-testid={`run-${run.run_id}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            {statusIcon(run.status)}
                                            <span className="text-sm font-medium text-stone-800">{run.status === 'running' ? 'Running...' : run.status}</span>
                                            <span className="text-xs text-stone-400">
                                                {new Date(run.started_at).toLocaleDateString()} {new Date(run.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-stone-500">
                                            <span>{run.items_processed} items scanned</span>
                                            {run.insights?.length > 0 && (
                                                <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setShowInsightsModal(run)} data-testid={`view-insights-${run.run_id}`}>
                                                    <Eye className="w-3 h-3 mr-1" /> {run.insights.length} insights
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    {run.error && <p className="text-xs text-red-600 mt-1">{run.error}</p>}
                                    {run.insights?.length > 0 && (
                                        <div className="mt-2 space-y-1.5">
                                            {run.insights.slice(0, 3).map((insight, i) => (
                                                <div key={i} className="flex items-start gap-2 text-sm">
                                                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 mt-0.5 ${PRIORITY_COLORS[insight.priority] || PRIORITY_COLORS.medium}`}>
                                                        {insight.priority || 'medium'}
                                                    </span>
                                                    <span className="text-stone-700">{insight.summary}</span>
                                                </div>
                                            ))}
                                            {run.insights.length > 3 && <p className="text-xs text-stone-400 pl-8">+ {run.insights.length - 3} more insights</p>}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Full Insights Modal */}
            <Dialog open={!!showInsightsModal} onOpenChange={() => setShowInsightsModal(null)}>
                <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="insights-modal">
                    <DialogHeader>
                        <DialogTitle>Insights — {showInsightsModal?.heartbeat_name}</DialogTitle>
                        <DialogDescription>
                            {showInsightsModal && `${new Date(showInsightsModal.started_at).toLocaleDateString()} — ${showInsightsModal.items_processed} items analyzed`}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {(showInsightsModal?.insights || []).map((insight, i) => (
                            <div key={i} className="p-3 rounded-lg border border-stone-100 bg-stone-50" data-testid={`insight-${i}`}>
                                <div className="flex items-start gap-2 mb-1">
                                    <span className={`px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${PRIORITY_COLORS[insight.priority] || PRIORITY_COLORS.medium}`}>
                                        {insight.priority || 'medium'}
                                    </span>
                                    <p className="font-medium text-sm text-stone-800">{insight.summary}</p>
                                </div>
                                {insight.details && <p className="text-sm text-stone-600 pl-12 mt-1">{insight.details}</p>}
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* ── Create/Edit Heartbeat Modal ────────────────────────────────────────── */
function HeartbeatModal({ open, onClose, existing, builtins, getAuthHeader, onSaved }) {
    const [form, setForm] = useState({
        name: '', description: '', builtin_prompt: '', prompt_template: '',
        schedule: { frequency: 'daily', hour: 8, minute: 0, day_of_week: 0, day_of_month: 1 },
        scope: { target: 'deals', filters: {} },
        is_active: true,
    });
    const [loading, setLoading] = useState(false);
    const [promptMode, setPromptMode] = useState('builtin'); // builtin | custom

    useEffect(() => {
        if (open) {
            if (existing) {
                setForm({
                    name: existing.name || '',
                    description: existing.description || '',
                    builtin_prompt: existing.builtin_prompt || '',
                    prompt_template: existing.prompt_template || '',
                    schedule: existing.schedule || { frequency: 'daily', hour: 8, minute: 0, day_of_week: 0, day_of_month: 1 },
                    scope: existing.scope || { target: 'deals', filters: {} },
                    is_active: existing.is_active !== false,
                });
                setPromptMode(existing.prompt_template ? 'custom' : 'builtin');
            } else {
                setForm({
                    name: '', description: '', builtin_prompt: 'stale_deal_analysis', prompt_template: '',
                    schedule: { frequency: 'daily', hour: 8, minute: 0, day_of_week: 0, day_of_month: 1 },
                    scope: { target: 'deals', filters: {} },
                    is_active: true,
                });
                setPromptMode('builtin');
            }
        }
    }, [open, existing]);

    const handleSubmit = async () => {
        if (!form.name) return toast.error('Name is required');
        setLoading(true);
        try {
            const payload = {
                ...form,
                prompt_template: promptMode === 'custom' ? form.prompt_template : null,
                builtin_prompt: promptMode === 'builtin' ? form.builtin_prompt : null,
            };
            if (existing) {
                await axios.put(`${API}/heartbeat/${existing.heartbeat_id}`, payload, { headers: getAuthHeader() });
                toast.success('Automation updated');
            } else {
                await axios.post(`${API}/heartbeat/`, payload, { headers: getAuthHeader() });
                toast.success('Automation created');
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="heartbeat-modal">
                <DialogHeader>
                    <DialogTitle>{existing ? 'Edit' : 'New'} AI Automation</DialogTitle>
                    <DialogDescription>Configure an automated AI analysis of your CRM data</DialogDescription>
                </DialogHeader>
                <div className="space-y-5 py-2">
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase">Name</Label>
                        <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Weekly Deal Risk Scan" className="mt-1" data-testid="hb-name" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase">Description</Label>
                        <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Scans stale deals every Monday..." className="mt-1" data-testid="hb-description" />
                    </div>

                    {/* AI Prompt */}
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase mb-2 block">AI Analysis Type</Label>
                        <div className="flex gap-2 mb-3">
                            <button onClick={() => setPromptMode('builtin')}
                                className={`px-3 py-1.5 text-xs rounded-lg border font-medium ${promptMode === 'builtin' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200'}`}>
                                Built-in Prompts
                            </button>
                            <button onClick={() => setPromptMode('custom')}
                                className={`px-3 py-1.5 text-xs rounded-lg border font-medium ${promptMode === 'custom' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200'}`}>
                                Custom Prompt
                            </button>
                        </div>
                        {promptMode === 'builtin' ? (
                            <Select value={form.builtin_prompt} onValueChange={v => setForm({ ...form, builtin_prompt: v })}>
                                <SelectTrigger data-testid="hb-builtin"><SelectValue placeholder="Select analysis type..." /></SelectTrigger>
                                <SelectContent>
                                    {builtins.map(b => <SelectItem key={b.key} value={b.key}>{b.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Textarea value={form.prompt_template} onChange={e => setForm({ ...form, prompt_template: e.target.value })}
                                placeholder="Analyze the following CRM data and provide insights...&#10;&#10;Use {data} as placeholder for the CRM data."
                                rows={4} className="font-mono text-xs" data-testid="hb-custom-prompt" />
                        )}
                    </div>

                    {/* Schedule */}
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase mb-2 block">Schedule</Label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs text-stone-400">Frequency</Label>
                                <Select value={form.schedule.frequency} onValueChange={v => setForm({ ...form, schedule: { ...form.schedule, frequency: v } })}>
                                    <SelectTrigger className="mt-1" data-testid="hb-frequency"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="hourly">Hourly</SelectItem>
                                        <SelectItem value="daily">Daily</SelectItem>
                                        <SelectItem value="weekly">Weekly</SelectItem>
                                        <SelectItem value="monthly">Monthly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {form.schedule.frequency !== 'hourly' && (
                                <div>
                                    <Label className="text-xs text-stone-400">Time (UTC)</Label>
                                    <Input type="time" value={`${String(form.schedule.hour).padStart(2, '0')}:${String(form.schedule.minute).padStart(2, '0')}`}
                                        onChange={e => { const [h, m] = e.target.value.split(':'); setForm({ ...form, schedule: { ...form.schedule, hour: parseInt(h), minute: parseInt(m) } }); }}
                                        className="mt-1" data-testid="hb-time" />
                                </div>
                            )}
                            {form.schedule.frequency === 'weekly' && (
                                <div>
                                    <Label className="text-xs text-stone-400">Day of Week</Label>
                                    <Select value={String(form.schedule.day_of_week || 0)} onValueChange={v => setForm({ ...form, schedule: { ...form.schedule, day_of_week: parseInt(v) } })}>
                                        <SelectTrigger className="mt-1" data-testid="hb-day-of-week"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            {form.schedule.frequency === 'monthly' && (
                                <div>
                                    <Label className="text-xs text-stone-400">Day of Month</Label>
                                    <Input type="number" min="1" max="28" value={form.schedule.day_of_month || 1}
                                        onChange={e => setForm({ ...form, schedule: { ...form.schedule, day_of_month: parseInt(e.target.value) || 1 } })}
                                        className="mt-1" data-testid="hb-day-of-month" />
                                </div>
                            )}
                            {form.schedule.frequency === 'hourly' && (
                                <div>
                                    <Label className="text-xs text-stone-400">Minute of Hour</Label>
                                    <Input type="number" min="0" max="59" value={form.schedule.minute || 0}
                                        onChange={e => setForm({ ...form, schedule: { ...form.schedule, minute: parseInt(e.target.value) || 0 } })}
                                        className="mt-1" data-testid="hb-minute" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Scope */}
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase mb-2 block">Data Scope</Label>
                        <Select value={form.scope.target} onValueChange={v => setForm({ ...form, scope: { ...form.scope, target: v } })}>
                            <SelectTrigger data-testid="hb-scope-target"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="deals">Deals (Pipeline)</SelectItem>
                                <SelectItem value="contacts">Contacts</SelectItem>
                                <SelectItem value="accounts">Companies</SelectItem>
                                <SelectItem value="activities">Activities & Tasks</SelectItem>
                            </SelectContent>
                        </Select>
                        {form.scope.target === 'deals' && (
                            <div className="mt-2">
                                <Label className="text-xs text-stone-400">Filter by Stage (optional)</Label>
                                <Select value={form.scope.filters?.stage || 'all'} onValueChange={v => setForm({ ...form, scope: { ...form.scope, filters: { ...form.scope.filters, stage: v === 'all' ? undefined : v } } })}>
                                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Stages</SelectItem>
                                        <SelectItem value="qualification">Qualification</SelectItem>
                                        <SelectItem value="discovery">Discovery</SelectItem>
                                        <SelectItem value="proposal">Proposal</SelectItem>
                                        <SelectItem value="negotiation">Negotiation</SelectItem>
                                        <SelectItem value="closing">Closing</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {form.scope.target === 'accounts' && (
                            <div className="mt-2">
                                <Label className="text-xs text-stone-400">Health Score Below (optional)</Label>
                                <Input type="number" min="0" max="100" placeholder="60"
                                    value={form.scope.filters?.health_score_lt || ''}
                                    onChange={e => setForm({ ...form, scope: { ...form.scope, filters: { ...form.scope.filters, health_score_lt: e.target.value ? parseInt(e.target.value) : undefined } } })}
                                    className="mt-1" />
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={loading} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="hb-submit">
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {existing ? 'Save Changes' : 'Create Automation'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
